import { useCallback, useEffect, useRef, useState } from 'react';

import { IUtmParseTask, TASK_STATUS } from './types';
import { getApiErrorMessage } from '../../../../../helpers/utils/ui';
import { utmSchemesApi } from '../../../../../shared/common/api/endpoints/utmSchemesApi';

/** Как часто спрашиваем статус задачи. Ограничения на частоту у бэкенда нет */
const POLL_INTERVAL_MS = 5000;
/**
 * Когда перестать спрашивать. Демон выполняет задачи по одной, самая большая школа —
 * около 7 минут; если за полчаса статус не изменился, показываем «не дождались».
 */
const POLL_TIMEOUT_MS = 30 * 60 * 1000;

const isFinished = (task: IUtmParseTask | null): boolean =>
    !task || task.status === TASK_STATUS.done || task.status === TASK_STATUS.failed;

/**
 * Следит за задачей переразбора: опрашивает статус, пока задача не завершится,
 * и сам останавливается по таймауту.
 *
 * @param schoolId школа, в контексте которой запрашиваем задачу
 * @param onFinished вызывается один раз, когда задача завершилась (успешно или с ошибкой)
 */
export const useParseTask = (
    schoolId: number | string | undefined,
    onFinished?: () => void
) => {
    const [ task, setTask ] = useState<IUtmParseTask | null>(null);
    const [ error, setError ] = useState('');
    const [ timedOut, setTimedOut ] = useState(false);

    // Ref'ы, чтобы интервал не пересоздавался на каждый ответ
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const deadlineRef = useRef(0);
    const onFinishedRef = useRef(onFinished);
    onFinishedRef.current = onFinished;

    const stopPolling = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const poll = useCallback(async (taskId: number) => {
        if (!schoolId) return;

        try {
            const response = await utmSchemesApi.getTask(schoolId, taskId);
            const fresh = (response.data?.data ?? null) as IUtmParseTask | null;
            setTask(fresh);

            if (isFinished(fresh)) {
                stopPolling();
                onFinishedRef.current?.();
            } else if (Date.now() > deadlineRef.current) {
                // Задача жива, но слишком долго — перестаём дёргать бэкенд
                stopPolling();
                setTimedOut(true);
            }
        } catch (err) {
            // Разовый сбой сети не должен убивать слежение — гасим только текст ошибки
            setError(getApiErrorMessage(err, 'Не удалось получить статус задачи'));
        }
    }, [ schoolId, stopPolling ]);

    /** Начать следить за задачей: сразу показываем её и запускаем опрос, если не завершена */
    const watch = useCallback((next: IUtmParseTask | null) => {
        setTask(next);
        setError('');
        setTimedOut(false);
        stopPolling();

        if (!next || isFinished(next)) return;

        deadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
        timerRef.current = setInterval(() => {
            poll(next.id);
        }, POLL_INTERVAL_MS);
    }, [ poll, stopPolling ]);

    /** Забыть задачу (например, при закрытии окна) */
    const reset = useCallback(() => {
        stopPolling();
        setTask(null);
        setError('');
        setTimedOut(false);
    }, [ stopPolling ]);

    useEffect(() => stopPolling, [ stopPolling ]);

    return {
        task,
        error,
        timedOut,
        /** Задача поставлена и ещё не завершилась */
        isRunning: Boolean(task) && !isFinished(task),
        watch,
        reset,
    };
};
